import { useMutation } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'

import {
  getGetOngoingAssessmentQueryKey,
  getListOngoingAssessmentsQueryKey,
} from '@/api/assessments.hook'
import { User } from '@/gen/customer/management/v1/service_pb'
import { removeOngoingAssessmentAssignee } from '@/gen/inventory/v1/assessment_service-AssessmentService_connectquery'
import {
  GetOngoingAssessmentResponse,
  ListOngoingAssessmentsResponse,
  RemoveOngoingAssessmentAssigneeRequest,
} from '@/gen/inventory/v1/assessment_service_pb'
import { OngoingAssessment } from '@/gen/inventory/v1/ongoing_assessment_pb'

export const useOptimisticRemoveOngoingAssessmentAssignee = (companyId?: string) => {
  const queryClient = useQueryClient()
  const queryKey = getListOngoingAssessmentsQueryKey(companyId)
  const queryKeyWithoutCompanyId = getListOngoingAssessmentsQueryKey()

  const queryKeys = [queryKey, queryKeyWithoutCompanyId]

  return useMutation(removeOngoingAssessmentAssignee, {
    onMutate: async (variables) => {
      const typedVariables = variables as RemoveOngoingAssessmentAssigneeRequest
      const getOngoingAssessmentKey = typedVariables.ongoingAssessmentId
        ? getGetOngoingAssessmentQueryKey(typedVariables.ongoingAssessmentId)
        : undefined

      const previousDataMap = new Map<unknown[], ListOngoingAssessmentsResponse>()
      let previousGetOngoingAssessmentData: GetOngoingAssessmentResponse | undefined

      for (const key of queryKeys) {
        await queryClient.cancelQueries({ queryKey: key })

        const previousData = queryClient.getQueryData<ListOngoingAssessmentsResponse>(key)

        if (previousData) {
          previousDataMap.set(key, previousData)

          const optimisticData = updateOngoingAssessmentsData(previousData, typedVariables)
          queryClient.setQueryData(key, optimisticData)
        }
      }

      if (getOngoingAssessmentKey) {
        await queryClient.cancelQueries({ queryKey: getOngoingAssessmentKey })

        previousGetOngoingAssessmentData =
          queryClient.getQueryData<GetOngoingAssessmentResponse>(getOngoingAssessmentKey)

        if (previousGetOngoingAssessmentData) {
          const optimisticGetData = applyNewDataOnOngoingAssessment(
            previousGetOngoingAssessmentData,
            typedVariables,
          )
          queryClient.setQueryData(getOngoingAssessmentKey, optimisticGetData)
        }
      }

      return { previousDataMap, previousGetOngoingAssessmentData, getOngoingAssessmentKey }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousDataMap) {
        context.previousDataMap.forEach((previousData, key) => {
          queryClient.setQueryData(key, previousData)
        })
      }

      if (context?.getOngoingAssessmentKey && context.previousGetOngoingAssessmentData) {
        queryClient.setQueryData(
          context.getOngoingAssessmentKey,
          context.previousGetOngoingAssessmentData,
        )
      }
    },
    onSettled: async (_data, _error, variables) => {
      const typedVariables = variables as RemoveOngoingAssessmentAssigneeRequest
      const getOngoingAssessmentKey = typedVariables.ongoingAssessmentId
        ? getGetOngoingAssessmentQueryKey(typedVariables.ongoingAssessmentId)
        : undefined

      await Promise.all([
        ...queryKeys.map((key) => queryClient.invalidateQueries({ queryKey: key })),
        ...(getOngoingAssessmentKey
          ? [queryClient.invalidateQueries({ queryKey: getOngoingAssessmentKey })]
          : []),
      ])
    },
  })
}

const updateOngoingAssessmentsData = (
  oldData: ListOngoingAssessmentsResponse,
  variables: RemoveOngoingAssessmentAssigneeRequest,
): ListOngoingAssessmentsResponse => {
  const { ongoingAssessmentId, userId } = variables

  const updatedOngoingAssessments = oldData.ongoingAssessments.map(
    (assessment: OngoingAssessment) => {
      if (assessment.id !== ongoingAssessmentId) {
        return assessment
      }

      return {
        ...assessment,
        assignees: assessment.assignees.filter((assignee: User) => assignee.id !== userId),
      }
    },
  )

  return {
    ...oldData,
    ongoingAssessments: updatedOngoingAssessments,
  }
}

const applyNewDataOnOngoingAssessment = (
  oldData: GetOngoingAssessmentResponse,
  variables: RemoveOngoingAssessmentAssigneeRequest,
): GetOngoingAssessmentResponse => {
  const assessment = oldData.ongoingAssessment
  if (!assessment || assessment.id !== variables.ongoingAssessmentId) {
    return oldData
  }

  return {
    ...oldData,
    ongoingAssessment: {
      ...assessment,
      assignees: assessment.assignees.filter((assignee: User) => assignee.id !== variables.userId),
    },
  }
}
